/* eslint-disable jsx-a11y/media-has-caption */
"use client";

import { Pause, Play, SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { trackAudioPlayer } from "@/lib/analytics/ga-events";


interface AudioPlayerProps {
  audioUrl: string;
  title?: string;
  className?: string;
  playButtonClassName?: string;
  skipSeconds?: number;
  maxWidth?: string;
  minWidth?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  title,
  className,
  playButtonClassName,
  skipSeconds = 10,
  maxWidth,
  minWidth,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);


  const hasTrackedFirstPlay = useRef(false);

  const progressPercent = duration ? Math.min(Math.max((currentTime / duration) * 100, 0), 100) : 0;
  const volumePercent = isMuted ? 0 : Math.min(Math.max(volume * 100, 0), 100);

  const seekTrackStyle = {
    background: `linear-gradient(to right, hsl(var(--primary)) ${progressPercent}%, hsl(var(--secondary)) ${progressPercent}%)`,
  } satisfies React.CSSProperties;

  const volumeTrackStyle = {
    background: `linear-gradient(to right, hsl(var(--primary)) ${volumePercent}%, hsl(var(--secondary)) ${volumePercent}%)`,
  } satisfies React.CSSProperties;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("durationchange", updateDuration);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    if (audio.readyState >= 1) {
      updateDuration();
    }

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("durationchange", updateDuration);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
    };
  }, []);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
        trackAudioPlayer("pause", audioUrl);
      } else {
        await audio.play();
        trackAudioPlayer("play", audioUrl);

        if (!hasTrackedFirstPlay.current) {
          hasTrackedFirstPlay.current = true;
        }
      }
    } catch (error) {
      console.error("Failed to play audio:", error);
      setIsPlaying(false);
    }
  };

  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - skipSeconds);

    // Track skip backward event
    trackAudioPlayer("skip_backward", audioUrl);
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(duration, audio.currentTime + skipSeconds);

    // Track skip forward event
    trackAudioPlayer("skip_forward", audioUrl);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = Number.parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = Number.parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume || 0.5;
      setVolume(volume || 0.5);
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className={cn("bg-card border-border w-full rounded-xl border p-4 shadow-lg", className)}
      style={{
        maxWidth: maxWidth,
        minWidth: minWidth,
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        {/* Play/Pause and Skip Controls */}
        <div className="flex items-center gap-3 md:gap-2">
          <button
            onClick={togglePlayPause}
            className={cn(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform duration-300 ease-out hover:scale-110 hover:opacity-95 focus-visible:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 active:scale-95",
              playButtonClassName,
              isPlaying && "shadow-[0_0_1.5rem_rgba(255, 51, 71, 0.06)] ring-2 ring-primary/50",
            )}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" fill="currentColor" />
            ) : (
              <Play className="ml-0.5 h-4 w-4" fill="currentColor" />
            )}
          </button>

          <button
            onClick={skipBackward}
            className="text-muted-foreground hover:text-foreground flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ease-out hover:-translate-x-0.5 hover:bg-secondary focus-visible:translate-x-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label={`Skip backward ${skipSeconds} seconds`}
          >
            <SkipBack className="h-4 w-4" />
          </button>

          <button
            onClick={skipForward}
            className="text-muted-foreground hover:text-foreground flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ease-out hover:translate-x-0.5 hover:bg-secondary focus-visible:translate-x-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label={`Skip forward ${skipSeconds} seconds`}
          >
            <SkipForward className="h-4 w-4" />
          </button>

          <div className="text-muted-foreground ml-auto flex flex-shrink-0 items-center gap-1 text-sm tabular-nums md:hidden">
            <span>{formatTime(currentTime)}</span>
            <span>/</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="w-full md:flex md:flex-1 md:items-center md:gap-3">
          <div className="group/seek flex w-full items-center">
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="h-2 w-full cursor-pointer appearance-none rounded-full transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:transition-transform [&::-moz-range-thumb]:duration-300 [&::-moz-range-thumb]:ease-out focus-visible:[&::-moz-range-thumb]:scale-125 group-hover/seek:[&::-moz-range-thumb]:scale-110 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-300 [&::-webkit-slider-thumb]:ease-out focus-visible:[&::-webkit-slider-thumb]:scale-125 group-hover/seek:[&::-webkit-slider-thumb]:scale-110"
              style={seekTrackStyle}
            />
          </div>

          <div className="text-muted-foreground hidden flex-shrink-0 items-center gap-1 text-sm tabular-nums md:flex">
            <span className="urbanist font-semibold">{formatTime(currentTime)}</span>
            <span>/</span>
            <span className="urbanist font-semibold">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume Controls */}
        <div className="flex items-center gap-2 md:flex-shrink-0">
          <button
            onClick={toggleMute}
            className="text-muted-foreground hover:text-foreground flex-shrink-0 transition-transform duration-300 ease-out hover:scale-110 focus-visible:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-95"
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <div className="group/volume flex flex-1 items-center">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="h-1.5 max-w-[120px] flex-1 cursor-pointer appearance-none rounded-full transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:transition-transform [&::-moz-range-thumb]:duration-300 [&::-moz-range-thumb]:ease-out focus-visible:[&::-moz-range-thumb]:scale-125 group-hover/volume:[&::-moz-range-thumb]:scale-110 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-300 [&::-webkit-slider-thumb]:ease-out focus-visible:[&::-webkit-slider-thumb]:scale-125 group-hover/volume:[&::-webkit-slider-thumb]:scale-110"
              style={volumeTrackStyle}
            />
          </div>
        </div>
      </div>

      {title && <div className="text-foreground mt-3 text-balance text-sm font-medium">{title}</div>}
    </div>
  );
};

export default AudioPlayer;
